import { useEffect, useState, useRef } from 'react';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Form, Button, Modal, Image } from 'react-bootstrap';
import Breadcrumb from './Breadcrumb.js';
import { useNavigate } from 'react-router-dom';
import UserAddGeneral from './UserAddGeneral';
import UserAddAbout from './UserAddAbout';
import UserAddResidence from './UserAddResidence';
import api from './api/axiosInstance';
import UserAddAcademics from './UserAddAcademics';
import UserAddRegion from './UserAddRegion';
import Alert from 'react-bootstrap/Alert';
import UserAddJob from './UserAddJob.js';
import UserAddOccupation from './UserAddOccupation.js';
import UserAddCompany from './UserAddCompany.js';
import UserAddStudying from './UserAddStudying.js';
import icSucess from './assets/img/ic-celebration-circle.svg';

const UserAdd = () => {

    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [profileImage, setProfileImage] = useState(null);
    const [isImageDeleted, setIsImageDeleted] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [occupationsList, setOccupationsList] = useState([]);
    const regionRef = useRef();
    const occupationRef = useRef();
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const [successModalTitle, setSuccessModalTitle] = useState('Congratulations');
    const [successModalMessage, setSuccessModalMessage] = useState('You have successfully added a new user to aBROADIFY');

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                setCompanies(res.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };

        fetchCompanies();
    }, []);

    const [formData, setFormData] = useState({
        profileImage: '',
        firstName: '',
        familyName: '',
        gender: '',
        email: '',
        birthDate: '',
        phone: '',
        company: '',
        nationality: '',
        country: '',
        city: '',
        zipCity: '',
    });

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        
        if (!formData.birthDate.trim()) newErrors.birthDate = 'Birth Date is required';
        if (!formData.nationality.trim()) newErrors.nationality = 'Nationality is required';
        if (!formData.country.trim()) newErrors.country = 'Country is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.zipCity.trim()) newErrors.zipCity = 'Zip Code is required';

        setErrors(newErrors);
        setShowErrorMessage(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return Object.keys(newErrors).length === 0;
    };

    const [tickets, setTickets] = useState([]);
    useEffect(() => {
        const fetchTickets = async () => {
            try {
                // ✅ goes through interceptor; token auto-attached
                const res = await api.get('/api/tickets'); // ✅
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }   
        };
        fetchTickets();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent form from refreshing

        if (!validateForm()) return;

        const data = new FormData();

        if (profileImage && !isImageDeleted) {
            data.append('profileImage', profileImage);
        }

        data.append('firstName', formData.firstName);
        data.append('familyName', formData.familyName);
        data.append('gender', formData.gender);
        data.append('email', formData.email);
        data.append('birthDate', formData.birthDate);
        data.append('phone', formData.phone);

        if (formData.company) {
            data.append('company', formData.company);
        }
        
        if (formData.companyRole) {
            data.append('companyRole', formData.companyRole);
        }

        data.append('nationality', formData.nationality);
        data.append('country', formData.country);
        data.append('city', formData.city);
        data.append('zipCity', formData.zipCity);

        try {
            if (step === 1) {
                // ✅ Submit to create user on step 1
                const response = await api.post('/api/users', data);
                console.log('User created:', response.data.user);

                setFormData((prev) => ({
                    ...prev,
                    _id: response.data.user._id, // ✅ Store user ID
                }));

                // ✅ Move to step 2 after successful creation
                setShowErrorMessage(false)
                setStep(2);
            } else if (step === 2) {
                // ✅ Submit academics or other step 2 data here
                console.log('Step 2 submission logic here');
                navigate('/onboarding-user-list');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            if (
                error.response?.status === 400 &&
                error.response.data?.msg === 'User already exists'
            ) {
                setErrors((prev) => ({ ...prev, email: 'This email is already in use' }));
            } else {
                alert('An unexpected error occurred. Please try again.');
            }
        }
    };

    const handleAcademicSubmit = async () => {
        try {
            const payload = {
                highestDegree: formData.highestDegree,
                educationalInstitution: formData.educationalInstitution,
                academicsCountry: formData.academicsCountry,
                academicsCity: formData.academicsCity,
                studyDirection: formData.academicsStudyDirection,
            };

            const response = await api.put(`/api/users/${formData._id}/academics`, payload);
            console.log('✅ Academics updated:', response.data);

            // ✅ Move to step 3 after successful update
            setShowErrorMessage(false);
            setStep(3);
        } catch (err) {
            console.error('❌ Failed to update academic info:', err);
        }
    };

    const handleWorkPreferencesSubmit = async () => {
        const isRegionValid = regionRef.current?.validate();
        const isOccupationValid = occupationRef.current?.validate();

        // remove empty rows
        const occClean = (occupationsList || []).filter(o =>
            o && (o.major || o.subMajor || o.minor || o.unit || o.skill)
        ).slice(0, 3);

        if (!isRegionValid || !isOccupationValid) {
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        try {
            const payload = {
                countriesWorkPreferences: formData.countriesWorkPreferences,
                citiesWorkPreferences: formData.citiesWorkPreferences,
                languagesWorkPreferences: formData.languagesWorkPreferences,
                industryJobPreferences: formData.industryJobPreferences,
                employmentJobPreferences: formData.employmentJobPreferences,
                fteJobPreferences: formData.fteJobPreferences,
                jobTypeJobPreferences: formData.jobTypeJobPreferences,
                occupations: occupationsList,
                companyGlobalSize: formData.companyGlobalSize,
                companyLocalSize: formData.companyLocalSize,
                companyRating: formData.companyRating,
                fortune: formData.fortune,
                currentDegree: formData.currentDegree,
                futureDegree: formData.futureDegree,
                plannedGraduationYear: formData.plannedGraduationYear,
            };

            const response = await api.put(`/api/users/${formData._id}/work-preferences`, payload);
            console.log('✅ Work preferences updated:', response.data);
            setShowErrorMessage(false);
            setShowSuccessModal(true);
        } catch (err) {
            console.error('❌ Failed to update work preferences:', err);
        }
    };


    const handlePreviousStep = () => {
        console.log('handlePreviousStep'); // optional, keep if you want logs
        setShowErrorMessage(false);        // ✅ hide errors when going back (optional)
        setStep((prevStep) => Math.max(1, prevStep - 1)); // ✅ 3→2, 2→1, stays at 1
    };

    const handleDeleteImage = () => {
        setProfileImage(null);
        setFormData((prevData) => ({
            ...prevData,
            profileImagePreview: '',
        }));
        setIsImageDeleted(true); // ✅ mark as deleted
    };

    const handleChange = (e) => {
        
        const { name, value, files } = e.target;
        console.log('handleChange called with:', name, value);

        // Handle file input (e.g., profileImage upload)
        if (name === 'profileImage' && files && files.length > 0) {
            const file = files[0];
            const fileUrl = URL.createObjectURL(file); // Create a temporary file URL
            setProfileImage(file); // Store the file in state
            setFormData((prevData) => {
                const updatedData = {
                    ...prevData,
                    profileImagePreview: fileUrl, // Store the preview URL in formData
                };
                return updatedData;
            });
            return;
        }
    
        setFormData(prevData => ({
            ...prevData,
            [name]: value  // This assumes value is always correct
        }));
    };

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Form onSubmit={step === 2 ? handleSubmit : undefined}>
                        <Row>
                            <Col>
                                <Card>
                                    <Card.Body className="pt-0">
                                        <Row>
                                            <Col xs={12}>
                                                {step === 1 && (
                                                    <>
                                                        <Row>
                                                            <Col>
                                                                <UserAddGeneral
                                                                    formData={formData}
                                                                    profileImage={formData.profileImagePreview || profileImage}
                                                                    handleChange={handleChange}
                                                                    handleDeleteImage={handleDeleteImage}
                                                                    errors={errors}
                                                                    companies={companies}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row className="my-5">
                                                            <Col><h2>About</h2></Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddAbout
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    isEditPage={false}
                                                                    companies={companies}
                                                                />
                                                            </Col>
                                                            <Col>
                                                                <UserAddResidence
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    isEditPage={false}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}

                                                {step === 2 && (
                                                    <>
                                                        <Row>
                                                            <Col>
                                                                <UserAddGeneral
                                                                    formData={formData}
                                                                    profileImage={formData.profileImagePreview || profileImage}
                                                                    handleChange={handleChange}
                                                                    handleDeleteImage={handleDeleteImage}
                                                                    errors={errors}
                                                                    isEditPage={true} // ✅ Show as edit on step 2
                                                                    companies={companies}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row className="my-5">
                                                            <Col><h2>Academics</h2></Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddAcademics
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}

                                                {step === 3 && (
                                                    <>
                                                        <Row>
                                                            <Col>
                                                                <UserAddGeneral
                                                                    formData={formData}
                                                                    profileImage={formData.profileImagePreview || profileImage}
                                                                    handleChange={handleChange}
                                                                    handleDeleteImage={handleDeleteImage}
                                                                    errors={errors}
                                                                    isEditPage={true}
                                                                    companies={companies}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row className="my-5">
                                                            <Col><h2>Work Preferences</h2></Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddRegion
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    ref={regionRef}
                                                                    isEditPage={false}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddJob
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddOccupation
                                                                    ref={occupationRef}
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    occupationsList={occupationsList}
                                                                    onOccupationsChange={setOccupationsList}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddCompany
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <UserAddStudying
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        <Row className="buttons-row">
                            <Col>
                                <Button variant="outline-secondary" onClick={() => navigate('/onboarding-user-list')}>
                                    Cancel
                                </Button>

                                {step === 1 && (
                                    <Button variant="primary" onClick={handleSubmit}>
                                        Next
                                    </Button>
                                )}

                                {step === 2 && (
                                    <>
                                        <Button variant="outline-secondary" className="ms-2" onClick={handlePreviousStep}>
                                            Back
                                        </Button>
                                        <Button variant="primary" className="ms-2" onClick={handleAcademicSubmit}>
                                            Next
                                        </Button>
                                    </>
                                )}
                                
                                {step === 3 && (
                                    <>
                                        <Button variant="outline-secondary" className="ms-2" onClick={handlePreviousStep}>
                                            Back
                                        </Button>
                                        <Button variant="primary" className="ms-2" onClick={handleWorkPreferencesSubmit}>
                                            Submit
                                        </Button>
                                    </>
                                )}

                            </Col>
                        </Row>
                    </Form>
                </Container>
            </main>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton onClick={() => navigate('/onboarding-user-list')}>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">{successModalTitle}</h2>
                    <p className="text-center">{successModalMessage}</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button variant="primary" onClick={() => navigate('/onboarding-user-list')}>
                        Go to Users
                    </Button>
                </Modal.Footer>
            </Modal>;
        </>
    );
};

export default UserAdd;
